import ColorModeToggle from 'Components/ColorModeToggle'
import { logOutUser } from 'Services/common/Features/User/userSlice'
import { useAppDispatch, useTypedSelector } from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import Logout from '@mui/icons-material/Logout'
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useTheme
} from '@mui/material'
import { useColorScheme } from '@mui/material/styles'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FC, useState } from 'react'
import { t } from 'ttag'

const UserMenu: FC = () => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { mode, setMode } = useColorScheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { user, loading } = useTypedSelector(state => state.user)

  const isProfileMenuOpen = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const logOut = () => {
    dispatch(logOutUser())
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Successfully logged out.`)
      })
  }

  const isLoading = loading === 'pending'
  const userAvailable = !isLoading && user

  return (
    <Box>
      <IconButton onClick={handleClick} size="small">
        <Avatar
          sx={{
            backgroundColor: theme.palette.primary.main,
            width: 32,
            height: 32
          }}
        >
          {userAvailable ? (
            <Typography component="span">{user?.email.charAt(0).toUpperCase()}</Typography>
          ) : (
            <CircularProgress size={12} />
          )}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isProfileMenuOpen}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
                backgroundColor: theme.palette.primary.main
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {userAvailable && (
          <MenuItem style={{ pointerEvents: 'none' }}>
            <Avatar /> {user?.email}
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={event => {
            event.stopPropagation() // Prevent menu from closing
            const newMode = mode === 'dark' ? 'light' : 'dark'
            setMode(newMode)
          }}
        >
          <ListItemIcon>
            <DarkModeIcon color={mode === 'dark' ? 'primary' : 'disabled'} />
          </ListItemIcon>
          <ColorModeToggle />
        </MenuItem>
        <Divider />
        <MenuItem onClick={logOut}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t`Logout`}
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default UserMenu
